import React, { useEffect, useRef } from 'react';

const FIELD_WGSL = `
struct Params { time: f32, texel: vec2f, mouse: vec2f }

@group(0) @binding(0) var<uniform> params: Params;

fn filament(p: vec2f, mouseP: vec2f, t: f32, seed: f32, y0: f32) -> f32 {
  let travel = p.x * (2.05 + seed * 0.42) + t * (0.14 + seed * 0.03) + seed * 3.7;
  let path = y0
    + 0.036 * sin(travel)
    + 0.014 * sin(travel * 2.21 + seed)
    + 0.006 * sin(travel * 4.8 - t * 0.07);
  let reach = exp(-abs(p.x - mouseP.x) * 3.4);
  let pullY = (mouseP.y - path) * reach * 0.08;
  let pullX = (mouseP.x - p.x) * reach * 0.01;
  let d = abs(p.y - (path + pullY) + pullX * 0.10);
  return exp(-d * 96.0) + exp(-d * 16.0) * 0.13;
}

@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let aspect = params.texel.y / max(params.texel.x, 0.0001);
  let p = vec2f((uv.x - 0.5) * aspect, uv.y - 0.46);
  let mouseP = vec2f((params.mouse.x - 0.5) * aspect, params.mouse.y - 0.46);
  let t = params.time * 0.18;

  let a = filament(p, mouseP, t, 0.0, -0.04);
  let b = filament(p, mouseP, t, 1.15, 0.11);
  let c = filament(p, mouseP, t, 2.08, -0.17);
  let spark = exp(-length(p - mouseP) * 22.0) * 0.02;

  let copyKeep = mix(0.05, 1.0, smoothstep(0.12, 0.70, uv.x));
  let vignette = smoothstep(1.18, 0.28, length(p * vec2f(0.72, 1.12)));

  let ink = vec3f(0.035, 0.035, 0.038);
  var col = ink;
  col += vec3f(0.78, 0.80, 0.82) * a * 0.40;
  col += vec3f(0.86, 0.86, 0.88) * b * 0.26;
  col += vec3f(0.72, 0.74, 0.78) * c * 0.30;
  col += vec3f(0.84, 0.85, 0.87) * spark;
  col *= copyKeep * vignette;

  return vec4f(col, 1.0);
}
`;

const COLUMNS_WGSL = `
struct Params { time: f32, texel: vec2f, mouse: vec2f }

@group(0) @binding(0) var<uniform> params: Params;

fn filament(p: vec2f, mouseP: vec2f, t: f32, seed: f32, y0: f32) -> f32 {
  let travel = p.x * (2.05 + seed * 0.42) + t * (0.14 + seed * 0.03) + seed * 3.7;
  let path = y0
    + 0.036 * sin(travel)
    + 0.014 * sin(travel * 2.21 + seed)
    + 0.006 * sin(travel * 4.8 - t * 0.07);
  let reach = exp(-abs(p.x - mouseP.x) * 3.4);
  let pullY = (mouseP.y - path) * reach * 0.08;
  let pullX = (mouseP.x - p.x) * reach * 0.01;
  let d = abs(p.y - (path + pullY) + pullX * 0.10);
  return exp(-d * 96.0) + exp(-d * 16.0) * 0.13;
}

@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let aspect = params.texel.y / max(params.texel.x, 0.0001);
  let p = vec2f((uv.x - 0.5) * aspect, uv.y - 0.46);
  let mouseP = vec2f((params.mouse.x - 0.5) * aspect, params.mouse.y - 0.46);
  let t = params.time * 0.12;

  let a = filament(p, mouseP, t, 0.0, -0.04);
  let b = filament(p, mouseP, t, 1.15, 0.11);
  let c = filament(p, mouseP, t, 2.08, -0.17);
  let spark = exp(-length(p - mouseP) * 22.0) * 0.012;

  let leftKeep = mix(0.06, 1.0, smoothstep(0.04, 0.34, uv.x));
  let rightKeep = mix(0.06, 1.0, smoothstep(0.96, 0.66, uv.x));
  let copyKeep = min(leftKeep, rightKeep) * 0.52;
  let vignette = smoothstep(1.18, 0.28, length(p * vec2f(0.72, 1.12)));

  let ink = vec3f(0.035, 0.035, 0.038);
  var col = ink;
  col += vec3f(0.78, 0.80, 0.82) * a * 0.40;
  col += vec3f(0.86, 0.86, 0.88) * b * 0.26;
  col += vec3f(0.72, 0.74, 0.78) * c * 0.30;
  col += vec3f(0.84, 0.85, 0.87) * spark;
  col *= copyKeep * vignette;

  return vec4f(col, 1.0);
}
`;

const REST_MOUSE: [number, number] = [0.72, 0.42];
const COLUMNS_REST_MOUSE: [number, number] = [0.5, 0.4];
const POINTER_STIFFNESS = 0.016;
const POINTER_DAMPING = 0.78;
const INIT_RETRY_DELAYS_MS = [0, 60, 180, 480];

export type ClarityHeroFieldCopyKeep = 'left' | 'columns';

export type ClarityHeroFieldProps = {
  className?: string;
  copyKeep?: ClarityHeroFieldCopyKeep;
  hostSelector?: string;
};

type VgpuModule = typeof import('vgpu');

function prefersReducedMotion(): boolean {
  return (
    Boolean(window.matchMedia) && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function canUseWebGpu(): boolean {
  return 'gpu' in navigator && Boolean(navigator.gpu);
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

function waitForCanvasLayout(canvas: HTMLCanvasElement, signal: AbortSignal): Promise<void> {
  const ready = () => canvas.clientWidth > 0 && canvas.clientHeight > 0;

  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    let settled = false;
    let frame = 0;
    let timeout = 0;
    const observer = !window.ResizeObserver
      ? { disconnect() {} }
      : new window.ResizeObserver(() => {
          if (ready()) {
            requestAnimationFrame(() => finish(true));
          }
        });
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      if (ok) resolve();
      else reject(new DOMException('Aborted', 'AbortError'));
    };

    if ('observe' in observer) observer.observe(canvas);
    frame = requestAnimationFrame(() => {
      if (ready()) finish(true);
    });
    timeout = window.setTimeout(() => finish(true), 320);

    signal.addEventListener('abort', () => finish(false), { once: true });
  });
}

function pointerUv(host: HTMLElement, event: PointerEvent): [number, number] {
  const bounds = host.getBoundingClientRect();
  const width = Math.max(bounds.width, 1);
  const height = Math.max(bounds.height, 1);

  return [
    Math.min(1, Math.max(0, (event.clientX - bounds.left) / width)),
    Math.min(1, Math.max(0, (event.clientY - bounds.top) / height)),
  ];
}

function springToward(
  current: [number, number],
  target: [number, number],
  velocity: [number, number],
) {
  const nextVelocity: [number, number] = [
    (velocity[0] + (target[0] - current[0]) * POINTER_STIFFNESS) * POINTER_DAMPING,
    (velocity[1] + (target[1] - current[1]) * POINTER_STIFFNESS) * POINTER_DAMPING,
  ];

  const nextCurrent: [number, number] = [
    current[0] + nextVelocity[0],
    current[1] + nextVelocity[1],
  ];

  return {
    current: nextCurrent,
    velocity: nextVelocity,
  };
}

function copyPoint(point: [number, number]): [number, number] {
  return [point[0], point[1]];
}

type FieldOptions = {
  copyKeep: ClarityHeroFieldCopyKeep;
  hostSelector: string;
};

async function startField(
  canvas: HTMLCanvasElement,
  vgpu: VgpuModule,
  options: FieldOptions,
): Promise<() => void> {
  const restMouse = options.copyKeep === 'columns' ? COLUMNS_REST_MOUSE : REST_MOUSE;
  const source = options.copyKeep === 'columns' ? COLUMNS_WGSL : FIELD_WGSL;
  const gpu = await vgpu.init({ powerPreference: 'low-power' });
  gpu.onError(() => undefined);

  try {
    const canvasSurface = vgpu.surface(gpu, canvas, {
      alphaMode: 'opaque',
      clearColor: [0.035, 0.035, 0.038, 1],
      dpr: [1, 2],
    });
    const field = vgpu.effect(gpu, source, {
      set: { params: { texel: canvasSurface.texelSize, time: 0, mouse: restMouse } },
    });
    const stopResize = canvasSurface.onResize(() => {
      field.set({ params: { texel: canvasSurface.texelSize } });
    });

    field.set({ params: { texel: canvasSurface.texelSize, time: 0, mouse: restMouse } });
    vgpu.frame(gpu, (currentFrame) => {
      currentFrame.pass(canvasSurface, field);
    });
    canvas.dataset.fieldState = 'live';

    if (prefersReducedMotion()) {
      return () => {
        delete canvas.dataset.fieldState;
        stopResize();
        gpu.dispose();
      };
    }

    const found = canvas.closest(options.hostSelector) ?? canvas.parentElement;
    const host = found instanceof HTMLElement ? found : null;
    const pointer = {
      current: copyPoint(restMouse),
      target: copyPoint(restMouse),
      velocity: [0, 0] satisfies [number, number],
    };

    const onPointerMove: EventListener = (event) => {
      if (!host || !(event instanceof PointerEvent)) return;
      pointer.target = pointerUv(host, event);
    };
    const onPointerLeave: EventListener = () => {
      pointer.target = restMouse;
    };

    const target: EventTarget | null = host;
    target?.addEventListener('pointermove', onPointerMove, { passive: true });
    target?.addEventListener('pointerenter', onPointerMove, { passive: true });
    target?.addEventListener('pointerleave', onPointerLeave);

    let onScreen = canvas.getBoundingClientRect().height > 0;
    const observer = new IntersectionObserver((entries) => {
      onScreen = entries.some((entry) => entry.isIntersecting);
    });
    observer.observe(canvas);

    const time = vgpu.clock(gpu);
    const loop = vgpu.frameLoop(
      gpu,
      (frame) => {
        if (document.hidden || !onScreen) return;
        const next = springToward(pointer.current, pointer.target, pointer.velocity);
        pointer.current = next.current;
        pointer.velocity = next.velocity;
        field.set({ params: { time: time.time, mouse: pointer.current } });
        frame.pass(canvasSurface, field);
      },
      { fps: 28 },
    );

    return () => {
      delete canvas.dataset.fieldState;
      target?.removeEventListener('pointermove', onPointerMove);
      target?.removeEventListener('pointerenter', onPointerMove);
      target?.removeEventListener('pointerleave', onPointerLeave);
      observer.disconnect();
      loop.stop();
      stopResize();
      gpu.dispose();
    };
  } catch (error) {
    gpu.dispose();
    throw error;
  }
}

async function attachField(
  canvas: HTMLCanvasElement,
  vgpu: VgpuModule,
  signal: AbortSignal,
  options: FieldOptions,
): Promise<(() => void) | undefined> {
  if (!canUseWebGpu() || signal.aborted) return undefined;

  await waitForCanvasLayout(canvas, signal);
  if (signal.aborted) return undefined;

  for (let attempt = 0; attempt < INIT_RETRY_DELAYS_MS.length; attempt += 1) {
    const delay = INIT_RETRY_DELAYS_MS[attempt];
    if (delay > 0) await wait(delay, signal);
    if (signal.aborted) return undefined;

    try {
      return await startField(canvas, vgpu, options);
    } catch {
      if (attempt === INIT_RETRY_DELAYS_MS.length - 1) return undefined;
    }
  }

  return undefined;
}

export default function ClarityHeroField({
  className = 'clarity-home__field',
  copyKeep = 'left',
  hostSelector = '.clarity-home__hero',
}: ClarityHeroFieldProps = {}): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const controller = new AbortController();
    let dispose: (() => void) | undefined;

    void import('vgpu')
      .then((vgpu) => attachField(canvas, vgpu, controller.signal, { copyKeep, hostSelector }))
      .then((stop) => {
        if (controller.signal.aborted) {
          stop?.();
          return;
        }
        dispose = stop;
      })
      .catch(() => undefined);

    return () => {
      controller.abort();
      dispose?.();
    };
  }, [copyKeep, hostSelector]);

  return (
    <canvas
      aria-hidden="true"
      className={className}
      data-hero-field="vgpu"
      ref={canvasRef}
      style={{
        backgroundImage: "url('/images/clarity/hero-filaments.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
  );
}
